import getConfig from 'next/config';

import { type PublishArgs } from '@meetings/details/components/Publish/Publish.types';
import { LIMIT_FOR_SUGGESTED_MEMBERS } from '@meetings/details/details.constants';

const { publicRuntimeConfig } = getConfig();
const { AI_SERVER_URL } = publicRuntimeConfig;

export const attendee = {
    home: (roomSlug: string): string => `/rooms/${roomSlug}/home/`,

    meeting: (roomSlug: string, meetingId: string): string =>
        `/rooms/${roomSlug}/meetings/${meetingId}`,

    meetings: (slug: string): string => `/rooms/${slug}/meetings/`,

    meetingAgenda: (slug: string, meetingId: string): string =>
        `/rooms/${slug}/meetings/${meetingId}`,

    openMeetingDocument: (
        roomSlug: string,
        meetingId: string,
        agendaItemId: string,
        documentId: string
    ): string => `/rooms/${roomSlug}/meetings/${meetingId}/topic/${agendaItemId}/${documentId}`,

    digitalSignatureDocumentPage: (meetingId: string, roomId: string): string =>
        `/events/${meetingId}/minutes/digital-signature-request/preview/?room_id=${roomId}`
};

export const admin = {
    createMeeting: (roomSlug: string, filter: string, type: string): string =>
        `/administrator/rooms/${roomSlug}/meetings/${filter}/meeting/new/${type}/`,

    home: (roomSlug: string): string => `/administrator/rooms/${roomSlug}/home/`,

    meeting: (roomSlug: string, filter: string, meetingId: string, tab?: string): string =>
        `/administrator/rooms/${roomSlug}/meetings/${filter}/meeting/${[meetingId, tab]
            .filter(Boolean)
            .join('/')}`,

    meetingHistory: (roomSlug: string, filter: string, meetingId: string): string =>
        `/administrator/rooms/${roomSlug}/meetings/${filter}/meeting/${meetingId}/history/`,

    meetings: (roomSlug: string): string => `/administrator/rooms/${roomSlug}/meetings/`,

    meetingAgenda: (slug: string, filter: string, meetingId: string): string =>
        `/administrator/rooms/${slug}/meetings/${filter}/meeting/${meetingId}`,

    meetingMinutes: (slug: string, filter: string, meetingId: string): string =>
        `/administrator/rooms/${slug}/meetings/${filter}/meeting/${meetingId}/minutes/`,

    oldMeetings: (roomSlug: string): string => `/administrator/rooms/${roomSlug}/meetings/list/`,

    openMeetingDocument: (
        roomSlug: string,
        meetingId: string,
        hash: string,
        documentId: string,
        agendaItemId: string
    ): string =>
        `/administrator/rooms/${roomSlug}/meetings/${hash}/meeting/${meetingId}/topic/${agendaItemId}/${documentId}`,

    openMeetingDocumentCompass: (
        roomSlug: string,
        meetingId: string,
        hash: string,
        documentId: string
    ): string =>
        `/administrator/rooms/${roomSlug}/meetings/${hash}/meeting/${meetingId}/document/${documentId}/preview/`,

    openMeetingLink: (roomSlug: string, meetingId: string, hash: string, linkId: string): string =>
        `/administrator/rooms/${roomSlug}/meetings/${hash}/meeting/${meetingId}/link/${linkId}/preview/`,

    digitalSignatureDocumentPage: (meetingId: string, roomId: string): string =>
        `/events/${meetingId}/minutes/digital-signature-request/preview/?room_id=${roomId}&view=editor`
};

export const api = {
    createMeeting: (roomId: string): string => `/api/events/?utc_dates=1&room_id=${roomId}`,

    agendaTemplates: (roomId: string): string => `/api/rooms/${roomId}/templates/`,

    agendaTopics: (meetingId: string, roomId: string): string =>
        `/api/events/${meetingId}/agenda/?room_id=${roomId}`,

    agendaApprovals: (meetingId: string): string => `/api/events/${meetingId}/approvals/`,

    bulkAddGuestsToAgendaItem: (meetingId: string) =>
        `/api/events/${meetingId}/bulk_manage_guest_access/`,

    bulkManageMinutesViewerAccess: (meetingId: string) =>
        `/api/events/${meetingId}/bulk_manage_minutes_viewer_access/`,

    calendarFeed: (): string => `/api/calendar_feeds/`,

    checkOptimization: (): string => `${AI_SERVER_URL}/meeting/optimization-alert/`,

    cloneMeeting: (meetingId: string): string => `/api/events/${meetingId}/clone/`,

    createMinuteNote: (roomId: string): string => `/api/events/minutes/notes/?room_id=${roomId}`,

    createDecision: (): string => '/api/events/minutes/decisions/',

    createTask: (): string => '/api/events/minutes/tasks/',

    createShortLink: (meetingId: string): string => `/api/events/${meetingId}/short_link/`,

    updateDecision: (id: string): string => `/api/events/minutes/decisions/${id}/`,

    updateTask: (id: string): string => `/api/events/minutes/tasks/${id}/`,

    contributions: (): string => '/api/contributions/',

    contributionRequests: (
        roomId: string,
        options?: { includeClosed?: boolean; mode?: string; filterEvents?: string }
    ): string => {
        const params = new URLSearchParams();
        params.append('room_id', roomId);
        if (options?.includeClosed) {
            params.append('include_closed', 'true');
        }
        if (options?.mode) {
            params.append('mode', options.mode);
        }
        if (options?.filterEvents) {
            params.append('filter_events', options.filterEvents);
        }
        return `/api/contribution_requests/?${params.toString()}`;
    },

    copyDocument: (meetingId: string): string => `/api/events/${meetingId}/documents/copy/`,

    attachDocument: (meetingId: string, agendaItemId: string): string =>
        `/api/events/${meetingId}/topics/${agendaItemId}/attach/`,

    deleteCalendarFeed: (calendarId: string): string => `/api/calendar_feeds/${calendarId}/`,

    deleteMeeting: (meetingId: string): string => `/api/events/${meetingId}/`,

    disableFeedbackForm: (formId: string) => `/api/meeting_feedback/surveys/${formId}/`,

    enableFeedbackForm: () => '/api/meeting_feedback/surveys/',

    feedbackFormAnswers: (meetingId: string, roomId: string) =>
        `/api/meeting_feedback/answers/?room_id=${roomId}&event_id=${meetingId}`,

    feedbackFormAggregatedAnswers: (formId: string) => `/api/meeting_feedback/surveys/${formId}/`,

    feedbackFormQuestions: () => '/api/meeting_feedback/questions/',

    feedbackFormSubmitAnswers: () => '/api/meeting_feedback/answers/',

    loadMeetingDocuments: (meetingId: string): string => `/api/events/${meetingId}/documents/`,

    loadMeetingDocumentLabels: (roomId: string): string =>
        `/api/events/documents/labels/?room_id=${roomId}`,

    loadAttendance: (activeRoomId: string, meetingId: string): string =>
        `/api/events/minutes/attendance/?limit=99999&mode=draft&room_id=${activeRoomId}&filter_events=${meetingId}`,

    loadMeetingStates: (meetingId: string): string => `/api/events/${meetingId}/state/`,

    meetingDetails: (
        meetingId: string,
        options?: { includeInactiveMembers?: boolean; mode?: string }
    ): string => {
        const params = new URLSearchParams();
        params.append('utc_dates', '1');
        if (options?.includeInactiveMembers ?? true) {
            params.append('include_inactive', 'true');
        }
        if (options?.mode) {
            params.append('mode', options.mode);
        }
        return `/api/events/${meetingId}/?${params.toString()}`;
    },

    meetingNote: (id: string): string => `/api/whiteboards/${id}/preview/`,

    meetingInvitation: (id: string, roomId: string) =>
        `/api/events/${id}/invitation_document/?room_id=${roomId}`,

    meetingNotificationMembers: '/api/members/?offset=0&limit=99999',

    meetingHistory: (id: string, options?: { orderBy: string }): string => {
        const params = new URLSearchParams();
        if (options?.orderBy) {
            params.append('order_by', options?.orderBy);
        }
        return `/api/events/${id}/history/?limit=99999&${params.toString()}`;
    },

    downloadMeetingHistory: (id: string): string => `/api/events/${id}/history/download/`,

    meetings: (): string => '/api/events/',

    minutes: (
        roomId: string,
        meetingId: string,
        options?: { mode?: string; limit?: string; combineText?: boolean }
    ): string => {
        return `/api/events/minutes/?limit=99999&mode=${
            options?.mode ?? 'draft'
        }&room_id=${roomId}&filter_events=${meetingId}&combine_text=${options?.combineText ?? false}`;
    },

    minutesApprovals: (meetingId: string): string => `/api/events/${meetingId}/minutes_approvals/`,

    latestAgendaItemMinutes: (agendaItemId: string): string =>
        `/api/events/minutes/${agendaItemId}/latest/`,

    notifyMeetingAnnotation: (documentId: string, meetingId: string): string =>
        `/api/events/${meetingId}/documents/${documentId}/notify/`,

    notifyMeetingNoteAnnotation: (documentId: string): string =>
        `/api/whiteboards/${documentId}/notify/`,

    notifyMeetingBookGeneration: (pollingUrl: string): string => `${pollingUrl}set/?notify=true`,

    postDecision: (): string => '/api/events/minutes/decisions/',

    postTask: (): string => '/api/events/minutes/tasks/',

    deleteTask: (id: string): string => `/api/events/minutes/tasks/${id}/`,

    deleteDecision: (id: string): string => `/api/events/minutes/decisions/${id}/`,

    deleteTemplate: (roomId: string, templateId: string) =>
        `/api/rooms/${roomId}/templates/${templateId}/`,

    deleteInvitation: (meetingId: string): string =>
        `/api/events/${meetingId}/invitation_document/`,

    publishAgenda: (meetingId: string): string => `/api/events/${meetingId}/publish_agenda/`,

    publishMinutes: (meetingId: PublishArgs | string): string =>
        `/api/events/${meetingId}/publish_minutes/`,

    generateMinutesPDF: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes_pdf/request/`,

    downloadMinutesPDF: (meetingId: string, fileId: string): string =>
        `/api/events/${meetingId}/minutes_pdf/${fileId}/download/`,

    downloadAllMeetingDocuments: (meetingId: string, roomId: string): string =>
        `/api/events/${meetingId}/documents/archive/?room_id=${roomId}&mode=draft`,

    predictAgendaItemType: (agendaItemTitle: string): string =>
        `${AI_SERVER_URL}/agendaitem/catgory/predict/?title=${agendaItemTitle}`,

    previewDocument: (documentId: string, meetingId: string): string =>
        `/api/events/${meetingId}/documents/${documentId}/preview/`,

    publishAgendaPdf: (meetingId: string, roomId: string): string =>
        `/api/events/${meetingId}/agenda/publish/?room_id=${roomId}`,

    publishMeetingBookPdf: (meetingId: string, roomId: string): string =>
        `/api/events/${meetingId}/boardbook/publish/?room_id=${roomId}`,

    setPendingVote: (agendaId: string): string => `/api/resolutions/${agendaId}/`,

    sendSignature: (loggedMemberId: string): string =>
        `/api/accounts/user/${loggedMemberId}/hand_signature/`,
    shareAgenda: (meetingId: string): string => `/api/events/${meetingId}/share_agenda/`,
    shareMinutes: (meetingId: string): string => `/api/events/${meetingId}/share_minutes/`,
    singleAgendaDocument: (meetingId: string, documentId: string): string =>
        `/api/events/${meetingId}/documents/${documentId}/`,

    singleAgendaItem: (meetingId: string, agendaItemId: string): string =>
        `/api/events/${meetingId}/topics/${agendaItemId}/`,

    unallocatedDocuments: (meetingId: string): string =>
        `/api/events/${meetingId}/documents/?in_agenda=false`,

    updateAgenda: (meetingId: string): string => `/api/events/${meetingId}/agenda/`,

    updateAttendance: (attendanceId: string): string =>
        `/api/events/minutes/attendance/${attendanceId}/`,

    updateTaskDecision: (type: string, id: string): string => `/api/events/minutes/${type}s/${id}/`,

    updateImportedTask: (agendaItemId: string, id: string, roomId: string, mode: string): string =>
        `/api/events/minutes/tasks/attached/agenda/${agendaItemId}/${id}/?room_id=${roomId}&mode=${mode}`,

    updateMeeting: (meetingId: string): string => `/api/events/${meetingId}/?utc_dates=1`,

    updateMinuteNote: (noteId: string, roomId: string): string =>
        `/api/events/minutes/notes/${noteId}/?room_id=${roomId}`,

    uploadMinuteNoteImage: () => '/api/images/notes/',

    downloadMinuteNoteImage: (imageId: string): string => `/api/images/notes/${imageId}/download/`,

    uploadMinuteLayoutImageUrl: (): string => `/api/images/document-layouts/minutes/`,

    getMinuteLayoutImageUrl: (imageId: string): string =>
        `/api/images/document-layouts/minutes/${imageId}/download`,

    uploadMeetingDocument: (meetingId: string, roomId: string): string =>
        `/api/events/${meetingId}/documents/?room_id=${roomId}`,

    reorderAgendaItem: (meetingId: string, agendaItemId: string): string =>
        `/api/events/${meetingId}/topics/${agendaItemId}/reorder/`,

    updateAgendaItemAccess: (meetingId: string, agendaItemId: string): string =>
        `/api/events/${meetingId}/topics/${agendaItemId}/`,

    updateAgendaDocumentAccess: (meetingId: string, documentId: string): string =>
        `/api/events/${meetingId}/documents/${documentId}/`,

    reorderMeetingDocument: (meetingId: string, documentId: string): string =>
        `/api/events/${meetingId}/documents/${documentId}/reorder/`,

    addAgendaItem: (meetingId: string): string => `/api/events/${meetingId}/topics/`,

    deleteAgendaItem: (meetingId: string, agendaItemId: string): string =>
        `/api/events/${meetingId}/topics/${agendaItemId}/`,

    replaceMeetingDocument: (meetingId: string): string =>
        `/api/events/${meetingId}/documents/replace/`,

    getExternalContent: (meetingId: string, contentId: string): string =>
        `/api/events/${meetingId}/external_content_sources/${contentId}/`,

    createExternalContent: (meetingId: string): string =>
        `/api/events/${meetingId}/external_content_sources/`,

    updateExternalContent: (meetingId: string, contentId: string): string =>
        `/api/events/${meetingId}/external_content_sources/${contentId}/`,

    deleteExternalContent: (meetingId: string, contentId: string): string =>
        `/api/events/${meetingId}/external_content_sources/${contentId}/`,

    docusignSigningUrl: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes/digital-signature-request/docusign_sign_url/`,

    requestSignature: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes/digital-signature-request/`,

    requestSignatureDownload: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes/digital-signature-request/download/`,

    notifySigners: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes/digital-signature-request/notify/`,

    signMinutes: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes/digital-signature-request/sign/`,

    previewMinutes: (meetingId: string): string =>
        `/api/events/${meetingId}/minutes/digital-signature-request/preview/`,

    updateAgendaClassificationLevel: (meetingId: string, topicId: string): string =>
        `/api/events/${meetingId}/topics/${topicId}/`,

    bulkAgendaCategoryUpdate: (meetingId: string): string =>
        `/api/events/${meetingId}/bulk_category_update/`,

    bulkAgendaDurationUpdate: (meetingId: string): string =>
        `/api/events/${meetingId}/bulk_duration_update/`,

    bulkAgendaDelete: (meetingId: string): string => `/api/events/${meetingId}/bulk_delete/`,

    bulkAgendaCreate: (meetingId: string): string => `/api/events/${meetingId}/bulk_create/`,

    reorderTasksAndDecisions: (agendaItemId: string): string =>
        `/api/events/minutes/${agendaItemId}/reorder/`,
    reorderAttachedTasks: (agendaItemId: string, roomId: string): string =>
        `/api/events/minutes/tasks/attached/agenda/${agendaItemId}/reorder/?room_id=${roomId}`,

    sendContributionRequest: (): string => '/api/contribution_requests/',

    updateContributionRequest: (id: string): string => `/api/contribution_requests/${id}/`,

    bulkAddMembers: (roomId: string): string => `/api/events/bulk/add_members/?room_id=${roomId}`,

    addMeetingCheckIn: (meetingId: string): string => `/api/events/${meetingId}/easy-check-in/`,

    bulkCloneMeetings: (): string => `/api/events/bulk/clone/`,

    bulkDeleteMeetings: (): string => `/api/events/bulk/delete/`,

    aggregatedMeetings: (roomId: string): string => `/api/events/aggregated/?room_id=${roomId}`,

    bulkDeleteUnallocatedMeetingDocuments: (meetingId: string): string =>
        `/api/events/${meetingId}/documents/bulk_delete/`,

    restoreDocument: (meetingId: string, meetingDocumentId: string): string =>
        `/api/events/${meetingId}/documents/${meetingDocumentId}/restore/`,

    bulkRestoreDocuments: (meetingId: string): string =>
        `/api/events/${meetingId}/documents/bulk_restore/`,

    meetingTranscriptsList: (meetingId: string): string => `/api/events/${meetingId}/transcripts/`,

    meetingTranscript: (meetingId: string, transcriptId: string): string =>
        `/api/events/${meetingId}/transcripts/${transcriptId}/`,

    meetingTranscriptDelete: (meetingId: string, transcriptId: string): string =>
        `/api/events/${meetingId}/delete_transcript/${transcriptId}/`,

    reorderMeetingTranscripts: (meetingId: string): string =>
        `/api/events/${meetingId}/transcripts/reorder/`,

    saveMinutesLayout: (meetingId: string): string =>
        `/api/events/${meetingId}/document-layouts/minutes/`,

    loadMinutesLayout: (meetingId: string): string =>
        `/api/events/${meetingId}/document-layouts/minutes/`,

    giveDocumentCopilotFeedback: (jobId: string): string => `/api/copilot/jobs/${jobId}/feedback/`,

    getDocumentCopilotSettings: (): string => `/api/copilot/settings/`,

    overallMeetingPreparedness: (meetingId: string) =>
        `/api/events/${meetingId}/meeting_preparedness_summary/`,

    personalMeetingPreparedness: (meetingId: string) =>
        `/api/events/${meetingId}/meeting_preparedness_personal/`,

    meetingPreparednessSettings: (meetingId: string) =>
        `/api/events/${meetingId}/meeting_preparedness_settings/`,

    meetingPreparednessReminder: (meetingId: string) =>
        `/api/events/${meetingId}/send_preparation_reminder/`,

    suggestedMembers: (roomId: string) =>
        `/api/contribution_requests/suggested_members/?room_id=${roomId}&limit=${LIMIT_FOR_SUGGESTED_MEMBERS}`,

    documentCopilotChatHistory: (documentId: string) =>
        `/api/copilot/history/?document=${documentId}`,

    clearDocumentCopilotChatHistory: (documentId: string) =>
        `/api/copilot/history/bulk_delete/?document=${documentId}`,

    docusignEnvelope: (taskId: string): string => `/api/docusign_envelopes/${taskId}/`,

    lockMeeting: (meetingId: string): string => `/api/events/${meetingId}/lock/`,

    unlockMeeting: (meetingId: string): string => `/api/events/${meetingId}/unlock/`
};

export const deeplinks = {
    openMeeting: (eventId: string): string => `/open/event/${eventId}/`,
    openAgendaItem: (agendaItemId: string): string => `/open/agenda_item/${agendaItemId}/`
};

export const documentCopilotRagJobs = {
    createDocumentCopilotRagJob: (): string => `/api/copilot/jobs_document_rag/`,
    getDocumentCopilotRagJob: (documentCopilotRagJobId: string): string =>
        `/api/copilot/jobs_document_rag/${documentCopilotRagJobId}/`,
    getAllDocumentCopilotRagJobs: (): string => `/api/copilot/jobs_document_rag/`,
    getDocumentCopilotRagJobStatus: (documentCopilotRagJobId: string): string =>
        `/api/copilot/jobs/${documentCopilotRagJobId}/status/`
};

export const copilotSmartLiteJobs = {
    createCopilotSmartLiteJob: (): string => `/api/copilot/jobs_smart_lite/`,
    getAllCopilotSmartLiteJobs: (): string => `/api/copilot/jobs_smart_lite/`,
    getCopilotSmartLiteJob: (copilotSmartLiteJobId: string): string =>
        `/api/copilot/jobs/${copilotSmartLiteJobId}/`,
    getCopilotSmartLiteJobStatus: (copilotSmartLiteJobId: string): string =>
        `/api/copilot/jobs/${copilotSmartLiteJobId}/status/`
};

export const copilotMinutesJobs = {
    createCopilotMinutesJob: (meetingId: string): string =>
        `/api/copilot/jobs_meetings/${meetingId}/generate_minutes/`,
    getCopilotMinutesJobStatus: (meetingId: string, jobIds: string[]): string =>
        `/api/copilot/jobs_meetings/${meetingId}/generate_minutes/status/?jobs=${jobIds.join(',')}`,
    getCopilotMinutesOverview: (meetingId: string): string =>
        `/api/copilot/jobs_meetings/${meetingId}/generate_minutes/newest/`,
    cancelCopilotMinutesJob: (meetingId: string): string =>
        `/api/copilot/jobs_meetings/${meetingId}/generate_minutes/revoke/`
};

export const copilotTranslationJobs = {
    createCopilotTranslationJob: (meetingId: string): string =>
        `/api/copilot/jobs_meetings/${meetingId}/translate/`,
    getCopilotTranslationJobsStatus: (jobIds: string[]): string =>
        `/api/copilot/jobs/bulk_status/?jobs=${jobIds.join(',')}`
};
